import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { PARTNER_ADMIN_GROUPS, PARTNER_MODULE_LABELS } from '../partner-admin/catalog.js';
import './cos-icon.js';
import '../design-system/cos-input.js';
import { ViewportController } from '../design-system/viewport-controller.js';
// Responsive Workstream B (roam-responsive-pwa-spec.md §7) — the schema
// explorer + data console are today's real view-only-on-small-screens
// entries; "discoverability over mystery" applies to this settings nav
// exactly as it does to the main shell rail.
import { capabilityFor } from '../responsive/capability-registry.js';

// The admin settings secondary nav. Replaces the old single horizontal
// strip (which crammed ~19 links into a wrapping row) with a grouped,
// filterable vertical sidebar — the Stripe/Linear settings pattern.
// Grows cleanly as new settings surfaces land: add an entry to the
// right group below.
//
// Links navigate via normal hrefs (the SPA router intercepts clicks),
// so this component holds no routing logic — it only knows the current
// path (for the active highlight) and a client-side filter string.
//
// 2026-07-30 (Paul) — reworked from an always-expanded flat list into a
// COLLAPSIBLE ACCORDION, matching /partner-admin's treatment (only the
// group owning the current route is expanded by default; a filter
// expands every group with a match; clicking a collapsed header expands
// it). The two admin surfaces now read as one product. Unlike
// /partner-admin, this nav's ~20 routes stay separate pages — the
// accordion is presentation only, not a `?s=` single-page rework.

interface AdminNavItem { href: string; label: string }
interface AdminNavGroup { label: string; items: AdminNavItem[] }

/** One accordion row: a group, the items to render under it (all of
 *  them, or only the filter-matching subset), and whether it's open. */
interface AccordionRow { label: string; items: AdminNavItem[]; expanded: boolean }

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
	{
		label: 'People & access',
		items: [
			{ href: '/admin/users', label: 'Users' },
			{ href: '/admin/timeoff', label: 'Time off' },
			{ href: '/admin/training', label: 'Training' },
		],
	},
	{
		label: 'Workflow',
		items: [
			{ href: '/admin/module-templates', label: 'Module templates' },
			{ href: '/admin/project-templates', label: 'Project templates' },
			{ href: '/admin/project-phases', label: 'Project phases' },
			{ href: '/admin/ticket-templates', label: 'Ticket templates' },
			{ href: '/admin/escalation-rules', label: 'Escalation rules' },
			{ href: '/admin/derivation-rules', label: 'Conversation rules' },
			{ href: '/admin/kb-article-types', label: 'KB article types' },
		],
	},
	{
		label: 'Customization',
		items: [
			{ href: '/admin/custom-fields', label: 'Custom fields' },
			{ href: '/admin/labels', label: 'Labels' },
			{ href: '/admin/chat-channels', label: 'Chat channels' },
		],
	},
	{
		// Commercial / plan settings per product (libraries see their own
		// usage in the Partner admin; only staff set the ceilings here).
		label: 'Products',
		items: [
			{ href: '/admin/preserve-limits', label: 'Preserve limits' },
			{ href: '/admin/preserve-ia-import', label: 'Internet Archive import' },
			{ href: '/admin/client-settings', label: 'Client settings' },
		],
	},
	{
		// Marcie arc 2a-3 — the partner-property lookup vocabularies.
		label: 'Partner properties',
		items: [
			{ href: '/admin/partner-ils-types', label: 'ILS types' },
			{ href: '/admin/partner-discovery-layers', label: 'Discovery layers' },
			{ href: '/admin/partner-consortiums', label: 'Consortiums' },
			{ href: '/admin/partner-purchase-types', label: 'Purchase types' },
			{ href: '/admin/partner-statuses', label: 'Partner statuses' },
			{ href: '/admin/partner-module-statuses', label: 'Module statuses' },
			{ href: '/admin/partner-zoom-config-types', label: 'Zoom config' },
			{ href: '/admin/partner-currencies', label: 'Currencies' },
		],
	},
	{
		label: 'Data',
		items: [
			{ href: '/admin/pls/upload', label: 'PLS data' },
			{ href: '/admin/pls/link-suggestions', label: 'PLS link suggestions' },
			{ href: '/admin/schema-explorer', label: 'Schema explorer' },
			{ href: '/admin/data-console', label: 'Data console' },
		],
	},
	{
		label: 'Monitoring',
		items: [
			{ href: '/admin/activity', label: 'Activity' },
			{ href: '/admin/audit', label: 'Audit log' },
			{ href: '/admin/triage-review', label: 'Triage review' },
			// Feedback is DELIBERATELY absent (Paul, 2026-07-30: "why do we
			// have two feedback sections... are they not repetitive").
			// /feedback and /admin/feedback render the SAME component with
			// the SAME props — capability comes from isAdmin()/isDispatcher()
			// on the user, not from which route you entered by — so anyone
			// who could reach the admin entry gets an identical screen from
			// the main-nav one. The /admin/feedback ROUTE is kept as a
			// redirect for old bookmarks; only the duplicate door is gone.
		],
	},
	{
		label: 'Integrations',
		items: [{ href: '/admin/integrations', label: 'QuickBooks' }],
	},
	{
		// Agent platform (PRD 11): the agents, their hosts, and the shared
		// company policy document.
		label: 'AI agents',
		items: [
			{ href: '/admin/agents', label: 'Agents' },
			// Which connector tools library staff may use (agent-mcp.md).
			{ href: '/admin/mcp-library-tools', label: 'Library tools' },
			// Mission playbooks (agent missions M5).
			{ href: '/admin/agent-playbooks', label: 'Playbooks' },
			// Decision models for workflow decisions (agent workflows W2).
			{ href: '/admin/decision-engines', label: 'Decision models' },
			// Workflow definitions and their editor (agent workflows W5).
			{ href: '/admin/workflows', label: 'Workflows' },
			// The GitHub App's repositories (agent workflows W4).
			{ href: '/admin/github', label: 'GitHub' },
		],
	},
	{
		label: 'Design',
		items: [
			{ href: '/admin/design-system', label: 'Design system' },
			{ href: '/admin/design-settings', label: 'Design settings' },
		],
	},
];

@customElement('cos-admin-nav')
export class CosAdminNav extends LitElement {
	@property({ type: String }) path = '';
	// The library's word for the people it serves, as a plural. Only read
	// so a change redraws the section labels built from activeCardholderTerm().
	@property({ attribute: false }) cardholderPlural = '';
	@state() private _filter = '';
	/** Which group is open. `undefined` = untouched, follow the current
	 *  route (so a deep link lands open, and /admin — matching no group —
	 *  leaves everything collapsed); `null` = the user collapsed it; a
	 *  label = the one they opened. Single-open — see _onHeaderClick. */
	@state() private _openGroup: string | null | undefined = undefined;
	private _viewport = new ViewportController(this);

	static styles = css`
		:host {
			display: block;
			position: sticky;
			top: var(--cos-space-6);
			align-self: start;
		}
		.title {
			font-size: var(--cos-font-size-xs);
			text-transform: uppercase;
			letter-spacing: 0.06em;
			color: var(--cos-text-tertiary);
			margin: 0 0 var(--cos-space-2);
			padding: 0 var(--cos-space-2);
		}
		.filter {
			display: block;
			margin-bottom: var(--cos-space-3);
		}
		.group + .group { margin-top: 2px; }
		.group-header {
			display: flex;
			align-items: center;
			gap: 6px;
			width: 100%;
			text-align: left;
			font: inherit;
			font-size: var(--cos-font-size-xs);
			font-weight: 600;
			text-transform: uppercase;
			letter-spacing: 0.04em;
			color: var(--cos-text-tertiary);
			background: none;
			border: none;
			border-radius: var(--cos-radius-sm);
			padding: 7px 10px;
			cursor: pointer;
		}
		.group-header cos-icon {
			flex: none;
			color: var(--cos-text-tertiary);
		}
		.group-header:hover { background: var(--cos-surface-raised); }
		.group-header:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: -2px;
		}
		/* Item treatment is a deliberate byte-for-byte match of
		   cos-partner-admin-page's .module-sections — the two admin
		   surfaces must be indistinguishable (Paul, 2026-07-30). The
		   selected item is NEUTRAL (raised surface + primary text + the
		   brand keyline), not the teal-tinted brand-50/brand-700 this
		   nav used to use on its own. Keep them in step if either moves. */
		.group-items {
			padding-left: var(--cos-space-3);
		}
		a {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--cos-space-2);
			width: 100%;
			box-sizing: border-box;
			text-align: left;
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-secondary);
			text-decoration: none;
			border-left: var(--cos-keyline-width) solid transparent;
			border-radius: 0 var(--cos-radius-sm) var(--cos-radius-sm) 0;
			padding: 7px 10px;
		}
		/* Responsive Workstream B — the gated-item marker (§7:
		   "discoverability over mystery"). Quiet, never a warning color. */
		.gate-marker {
			color: var(--cos-text-tertiary);
			opacity: 0.7;
			flex-shrink: 0;
		}
		a:hover {
			color: var(--cos-text-primary);
			background: var(--cos-surface-raised);
		}
		a.active {
			color: var(--cos-text-primary);
			font-weight: 600;
			background: var(--cos-surface-raised);
			border-left-color: var(--cos-brand-500);
		}
		a:focus-visible { outline: 2px solid var(--cos-brand-500); outline-offset: -2px; }
		.empty {
			color: var(--cos-text-tertiary);
			font-size: var(--cos-font-size-sm);
			padding: var(--cos-space-2);
		}
		/* Single-column admin (.admin-layout stacks at <=900px): the rail
		   sits ABOVE the content, so sticky would pin it while the page
		   scrolls underneath — with no backdrop, the two interleave
		   (compact QA, 2026-08-06). Static lets it scroll away. Media
		   queries go LAST (the Stage 19f ordering rule). */
		@media (max-width: 900px) {
			:host { position: static; }
		}
	`;

	private _isActive(href: string): boolean {
		return this.path === href || this.path.startsWith(href + '/');
	}

	// The open group follows the route: navigating to a section reveals it,
	// while /admin — which matches no group — leaves everything collapsed.
	// Only reacts to a real path change, so a manual collapse isn't undone
	// by an unrelated re-render.
	protected willUpdate(changed: Map<string, unknown>): void {
		if (changed.has('path')) {
			const active = this._activeGroupLabel();
			if (active) this._openGroup = active;
		}
	}

	private _activeGroupLabel(): string | undefined {
		return ADMIN_NAV_GROUPS.find(g => g.items.some(i => this._isActive(i.href)))?.label;
	}

	/** A6 (Marcie round 3) — a filter query ALSO searches the partner-
	 *  module admin sections (the /partner-admin catalog), because that
	 *  is where somebody searching "event" here expects Attend's Event
	 *  settings to turn up. Match on the section label, the module's
	 *  plain-language name, or its brand name; results link straight to
	 *  the scoped page. Rendering-only discoverability — the Partner
	 *  admin page keeps its own gates. */
	private _partnerAdminMatches(q: string): AdminNavItem[] {
		if (!q) return [];
		const out: AdminNavItem[] = [];
		for (const g of PARTNER_ADMIN_GROUPS) {
			const mod = PARTNER_MODULE_LABELS[g.product];
			const modMatch = mod.main.toLowerCase().includes(q) || mod.sub.toLowerCase().includes(q);
			for (const s of g.sections) {
				if (modMatch || s.label.toLowerCase().includes(q)) {
					out.push({ href: `/partner-admin?s=${s.key}`, label: `${s.label} · ${mod.main}` });
				}
			}
		}
		return out;
	}

	/** The accordion rows to render: every group, with the items +
	 *  expanded-state a filter query (or its absence) implies. With no
	 *  filter, only the group owning the current route — plus any the
	 *  user has manually opened — is expanded. While filtering, every
	 *  group with a match is forced open (and only matching items show). */
	private _rows(): AccordionRow[] {
		const q = this._filter.trim().toLowerCase();
		if (!q) {
			return ADMIN_NAV_GROUPS.map(g => ({
				label: g.label,
				items: g.items,
				expanded: g.label === (this._openGroup === undefined ? this._activeGroupLabel() : this._openGroup),
			}));
		}
		const rows: AccordionRow[] = [];
		for (const g of ADMIN_NAV_GROUPS) {
			const items = g.items.filter(i => i.label.toLowerCase().includes(q));
			if (items.length > 0) rows.push({ label: g.label, items, expanded: true });
		}
		const partnerMatches = this._partnerAdminMatches(q);
		if (partnerMatches.length > 0) rows.push({ label: 'Partner admin', items: partnerMatches, expanded: true });
		return rows;
	}

	// Toggle. Clicking the open group closes it; clicking another opens
	// that one and closes the previous (Paul, 2026-07-30 — a group that
	// could only ever open is a group you can never minimise).
	private _onHeaderClick(row: AccordionRow): void {
		this._openGroup = row.expanded ? null : row.label;
	}

	private _onFilter(value: string) {
		this._filter = value;
	}

	render() {
		const rows = this._rows();
		return html`
			<p class="title">Settings</p>
			<cos-input
				class="filter"
				type="search"
				label="Filter settings"
				hide-label
				placeholder="Filter settings…"
				.value=${this._filter}
				@cos:input=${(e: CustomEvent<{ value: string }>) => this._onFilter(e.detail.value)}></cos-input>
			${rows.length === 0
				? html`<p class="empty">No settings match “${this._filter}”.</p>`
				: rows.map(row => {
						const panelId = `admin-nav-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
						return html`
							<div class="group">
								<button
									type="button"
									class="group-header"
									aria-expanded=${row.expanded ? 'true' : 'false'}
									aria-controls=${panelId}
									@click=${() => this._onHeaderClick(row)}>
									<cos-icon name=${row.expanded ? 'chevron-down' : 'chevron-right'} size="14"></cos-icon>
									<span>${row.label}</span>
								</button>
								<div class="group-items" id=${panelId} ?hidden=${!row.expanded}>
									${row.items.map(i => {
										const gate = capabilityFor(i.href, this._viewport.tier);
										return html`
											<a href=${i.href}
												class=${this._isActive(i.href) ? 'active' : ''}
												aria-current=${this._isActive(i.href) ? 'page' : 'false'}>${i.label}${gate.level !== 'full'
													? html`<cos-icon
															class="gate-marker"
															name="monitor"
															size="13"
															title=${gate.note ?? 'Needs a larger screen'}></cos-icon>`
													: ''}</a>
										`;
									})}
								</div>
							</div>
						`;
					})}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-admin-nav': CosAdminNav;
	}
}
